import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Form from '@nineeng/forms';
import { Globe } from 'lucide-react';
import { LanguageSelect, Tabs } from '@repo/components';
import React, { useCallback, useMemo, useState } from 'react';
import { Settings, LanguageCode, CompanyPortalSettings } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useConfig } from 'src/hooks/useConfig';
import { getMultipleSettings, updateSettings } from 'src/api/admin/settings';
import {
	NotificationsSettings,
	FeatureFlagsSettings,
	UserDataSettings,
	PartnerPortalSettings,
	AccessAppSettings,
	PrincipalGroupLabelsSettings,
	LanguagesSettings,
	PersonalDashboardSettings,
} from '@repo/shared/domain';

type PossibleSettings =
	| NotificationsSettings['settings']
	| UserDataSettings['settings']
	| CompanyPortalSettings['settings']
	| PersonalDashboardSettings['settings']
	| PartnerPortalSettings['settings']
	| AccessAppSettings['settings']
	| FeatureFlagsSettings['settings']
	| PrincipalGroupLabelsSettings['settings']
	| LanguagesSettings['settings'];
export const Route = createFileRoute('/_admin/system/settings')({
	loader: async () => {
		const settingsToGet = [
			Settings.Notifications,
			Settings.UserData,
			Settings.CompanyPortal,
			Settings.PersonalDashboard,
			Settings.PartnerPortal,
			Settings.AccessApp,
			Settings.FeatureFlags,
			Settings.PrincipalGroupLabels,
			Settings.Languages,
		];

		const allSettings = await getMultipleSettings(settingsToGet);
		return { allSettings };
	},
	component: SettingsPage,
});

// Tab Components
function NotificationsTab({
	data: _data,
	onChange,
	isDemoEnv,
}: {
	data: NotificationsSettings['settings'];
	onChange: (data: NotificationsSettings['settings']) => void;
	isDemoEnv: boolean;
}) {
	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{ name: 'checkIn', rowLabel: 'Event Check-In', type: 'Checkbox' },
					{ name: 'checkOut', rowLabel: 'Event Check-Out', type: 'Checkbox' },
					{
						name: 'checkInOutExtraRecipients',
						rowLabel: 'Extra recipients',
						rowDescription: 'List of extra recipient that will receive check-in and check-out notifications',
						type: 'MultiFreeInput',
						toLowerCase: true,
					},
					{
						name: 'checkInOutTextMessages',
						rowLabel: 'Send notifications also via SMS',
						rowDescription: 'Check-In / Out notifications will also be sent by SMS when enabled',
						type: 'Checkbox',
					},
					...(isDemoEnv
						? [
								{
									name: 'ignoreDemoWhitelist',
									rowLabel: 'Ignore Demo Env Whitelist',
									rowDescription:
										'Enabling this will send all mails to users in this demo environment (e.g. role expiry notifications)',
									type: 'Checkbox',
								},
							]
						: []),
					{ name: 'eventGroupAdded', rowLabel: 'Event Company Added', type: 'Checkbox' },
					{ name: 'documentRejected', rowLabel: 'Document Rejected', type: 'Checkbox' },
				],
			},
		],
	};

	return (
		<div>
			<Form schema={form} onChange={onChange} originalValues={_data} />
		</div>
	);
}

function UserDataTab({
	data: _data,
	onChange,
	usesExternalZoneAccess,
}: {
	data: UserDataSettings['settings'];
	onChange: (data: UserDataSettings['settings']) => void;
	usesExternalZoneAccess?: boolean;
}) {
	const hasAgeError = _data.maxAge && _data.minAge && _data.minAge > _data.maxAge;
	const userDataForm = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					// Custom user properties are now typed ({ name, type }[]) and edited in the
					// admin app's Custom Properties page; the legacy MultiFreeInput (string[]) here
					// could not represent the typed shape and would corrupt the setting on save.
					{ name: 'showShortId', rowLabel: 'Show user Short ID on the user details page', type: 'Checkbox' },
					{
						name: 'flaggedCountries',
						rowLabel: 'Country-based review',
						rowDescription: 'Select the countries from which users should be automatically flagged for review.',
						type: 'AsyncAutoComplete',
						dataSource: '/api/data-sources/countries/data',
						instantSearch: true,
						multiple: true,
					},
					{
						name: 'blockedCountries',
						rowLabel: 'Country-based block',
						rowDescription: 'Select the countries from which users should be automatically blocked.',
						type: 'AsyncAutoComplete',
						dataSource: '/api/data-sources/countries/data',
						instantSearch: true,
						multiple: true,
					},
					{
						name: 'minAge',
						rowLabel: 'Minimum Age Requirement',
						rowDescription: 'Users under this age will be flagged for review.',
						type: 'NumberTextField',
						error: hasAgeError,
					},
					{
						name: 'maxAge',
						rowLabel: 'Maximum Age Requirement',
						rowDescription: 'Users over this age will be flagged for review.',
						type: 'NumberTextField',
						error: hasAgeError,
						helperText: hasAgeError && 'Minimum age must be less than maximum age',
					},
					...(usesExternalZoneAccess
						? [
								{
									name: 'externalZoneAccessUrl',
									rowLabel: 'External Zone Access URL',
									rowDescription: 'URL of the source tenant for fetching zone access data for read-only users',
									type: 'TextField',
								},
							]
						: []),
				],
			},
		],
	};

	return (
		<div>
			<Form schema={userDataForm} onChange={onChange} originalValues={_data} />
		</div>
	);
}

function PortalTab({
	data: _data,
	onChange,
}: {
	data: CompanyPortalSettings['settings'] | PersonalDashboardSettings['settings'] | PartnerPortalSettings['settings'];
	onChange: (
		data: CompanyPortalSettings['settings'] | PersonalDashboardSettings['settings'] | PartnerPortalSettings['settings'],
	) => void;
}) {
	const portalForm = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{ name: 'welcomeMessage', rowLabel: 'Welcome Message', type: 'MarkdownEditor' },
					{
						type: 'repeater',
						rowLabel: 'Help Links',
						name: 'helpLinks',
						fields: [
							{ type: 'TextField', name: 'label', label: 'Label', size: 'small' },
							{ type: 'TextField', name: 'url', label: 'URL', size: 'small', sx: { marginTop: 1 } },
						],
					},
					{
						type: 'repeater',
						rowLabel: 'Links',
						name: 'links',
						fields: [
							{ type: 'TextField', name: 'label', label: 'Label', size: 'small' },
							{ type: 'TextField', name: 'url', label: 'URL', size: 'small', sx: { marginTop: 1 } },
						],
					},
				],
			},
		],
	};

	return (
		<div>
			<Form schema={portalForm} onChange={onChange} originalValues={_data} />
		</div>
	);
}

function AccessAppTab({
	data: _data,
	languagesSettings,
	onChange,
}: {
	data: AccessAppSettings['settings'];
	onChange: (data: AccessAppSettings['settings']) => void;
	languagesSettings: LanguagesSettings['settings'];
}) {
	const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('en');

	const AccessAppSettingsLanguageSelect = () => {
		const availableLanguages = [
			languagesSettings.defaultLanguage,
			...(languagesSettings.enabledLanguages || []),
		].filter(Boolean) as LanguageCode[];
		return (
			<div>
				<LanguageSelect
					language={selectedLanguage}
					availableLanguages={availableLanguages}
					onLanguageChange={setSelectedLanguage}
					customTailwindClasses={{
						selectTrigger: { border: 'border-border', width: 'w-[180px]' },
						selectContent: { width: 'w-[180px]' },
					}}
					icon={<Globe className="w-4 h-4" />}
				/>
			</div>
		);
	};

	const accessAppForm = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{ type: 'Custom', name: 'languageSelect', Component: AccessAppSettingsLanguageSelect },
					{
						name: `${selectedLanguage}.welcomeMessage`,
						rowLabel: `Welcome Message (${selectedLanguage.toUpperCase()})`,
						type: 'MarkdownEditor',
					},
					{
						type: 'repeater',
						rowLabel: `Links (${selectedLanguage.toUpperCase()})`,
						name: `${selectedLanguage}.Links`,
						fields: [
							{ type: 'TextField', name: 'label', label: 'Label', size: 'small' },
							{ type: 'TextField', name: 'url', label: 'URL', size: 'small', sx: { marginTop: 1 } },
						],
					},
				],
			},
		],
	};

	return (
		<div>
			<Form schema={accessAppForm} onChange={onChange} originalValues={_data} />
		</div>
	);
}

function FeatureFlagsTab({
	data: _data,
	onChange,
}: {
	data: FeatureFlagsSettings['settings'];
	onChange: (data: FeatureFlagsSettings['settings']) => void;
}) {
	const getFeatureFlags = () => {
		const flags: Record<string, boolean> = {};
		const descriptions: Record<string, string> = {};

		const knownFlags: Record<string, string> = {
			USES_RFID: 'Enable badge (RFID) functionality',
			USES_RSID: 'Enable faceprint (RSID) functionality',
			USES_RANDOM_CHECKS: 'Enable random check functionality (ASW Vilvoorde)',
			USES_EMERGENCY_MODULE: 'Enable emergency module functionality (VOPAK)',
			AUTO_CHECKIN: 'Enable automatic check-in functionality (Suntory)',
			USES_RFID_ENC: 'Enable encrypted RFID functionality',
			USES_MISTRAL: 'Enable Mistral AI document parsing',
			COMPANY_PORTAL_REMOTE_ACTIONS: 'Enable remote actions in company portal',
			USES_COMPANY_API_CHECKS: 'Enable showing company API checks',
			USES_SUBCONTRACTING: 'Enable subcontracting functionality',
			USE_CHECKINATWORK: 'Enable Check-in at Work integration',
			USE_TENANT_SYNC: 'Enable tenant data sync',
			USES_PLAYIT: 'Enable Playit integration',
			IS_ALHEEMBOUW:
				'Enable Alheembouw-specific features (phone required instead of email in invite form, work activities configuration)',
			USES_WEBHOOKS: 'Enable webhook integrations for external systems',
			USES_EXTERNAL_ZONE_ACCESS: 'Enable external API access for user zone data',
			USES_PARTNER_PORTAL: 'Enable partner portal functionality',
			USES_TAGS: 'Enable event type tags functionality',
			USES_SUPERVISOR:
				'Enable supervisor functionality and supervisor portal - group based without IS_BASF, supervisor based on internal groups when IS_BASF is enabled',
			USES_VCA_SELF_EMPLOYED:
				'For self-employed (ZZP) companies: require VCA-VOL on (one of the) admin(s) instead of a VCA on the company, when the event type requires a VCA',
			USES_OWNER_EMAIL_DOMAIN_CHECK:
				'Enable owner email domain check functionality on creation of companies in subcontracting flow',
			USES_ANTI_PASSBACK: 'Enable anti-passback clearing on checkout and check-in actions',
			IS_BASF: 'Enable BASF-specific features (CC company admins on document rejection emails)',
			USES_WORKFORCE: 'Enable workforce functionality',
		};

		const existingFlags = _data || {};

		Object.entries(knownFlags).forEach(([flag, description]) => {
			flags[flag] = existingFlags[flag] ? true : false;
			descriptions[flag] = description;
		});

		return { flags, descriptions };
	};

	const featureFlags = getFeatureFlags();
	const flagEntries = Object.entries(featureFlags.flags);

	const featureFlagsForm = {
		sections: [
			{
				type: 'SettingsSection',
				fields: flagEntries.map(([flagName, flagValue]) => ({
					name: flagName,
					rowLabel: flagName,
					rowDescription: featureFlags.descriptions[flagName] || `Feature flag: ${flagName}`,
					type: 'Checkbox',
					value: flagValue,
				})),
			},
		],
	};

	return (
		<div>
			<Form schema={featureFlagsForm} onChange={onChange} originalValues={featureFlags.flags} />
		</div>
	);
}

function PrincipalGroupLabelsTab({
	data: _data,
	onChange,
}: {
	data: PrincipalGroupLabelsSettings['settings'];
	onChange: (data: PrincipalGroupLabelsSettings['settings']) => void;
}) {
	const principalGroupLabelsForm = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						type: 'repeater',
						rowLabel: 'Project Company Labels',
						name: 'principalGroupLabels',
						fields: [{ type: 'TextField', name: 'value', label: 'Responsible Company Label', size: 'small' }],
					},
				],
			},
		],
	};

	return (
		<div>
			<Form schema={principalGroupLabelsForm} onChange={onChange} originalValues={_data} />
		</div>
	);
}

const defaultTabIndex = 0;

function SettingsPage() {
	const { allSettings } = Route.useLoaderData();
	const queryClient = useQueryClient();
	const { config } = useConfig();
	const isDemoEnv = config?.isDemoEnv || false;

	const notificationsSettings = allSettings[Settings.Notifications].settings as NotificationsSettings['settings'];
	const userDataSettings = allSettings[Settings.UserData].settings as UserDataSettings['settings'];
	const companyPortalSettings = allSettings[Settings.CompanyPortal].settings as CompanyPortalSettings['settings'];
	const personalDashboardSettings = allSettings[Settings.PersonalDashboard]
		.settings as PersonalDashboardSettings['settings'];
	const partnerPortalSettings = allSettings[Settings.PartnerPortal].settings as PartnerPortalSettings['settings'];
	const accessAppSettings = allSettings[Settings.AccessApp].settings as AccessAppSettings['settings'];
	const featureFlagsSettings = allSettings[Settings.FeatureFlags].settings as FeatureFlagsSettings['settings'];
	const principalGroupLabelsSettings = allSettings[Settings.PrincipalGroupLabels]
		.settings as PrincipalGroupLabelsSettings['settings'];
	const languagesSettings = allSettings[Settings.Languages].settings as LanguagesSettings['settings'];

	const [activeTab, setActiveTab] = useState(defaultTabIndex);
	const [hasChanged, setChanged] = useState(false);

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Settings');

	const tabs = useMemo(
		() => [
			{ key: 'notifications', label: 'Notifications', data: notificationsSettings || {}, el: NotificationsTab },
			{ key: 'user-data', label: 'User Data', data: userDataSettings || {}, el: UserDataTab },
			{
				key: 'company-portal',
				label: 'Company Dashboard',
				data: companyPortalSettings || {},
				el: PortalTab,
			},
			{
				key: 'personal-dashboard',
				label: 'Personal Portal',
				data: personalDashboardSettings || {},
				el: PortalTab,
			},
			{
				key: 'partner-portal',
				label: 'Partner Dashboard',
				data: partnerPortalSettings || {},
				el: PortalTab,
			},
			{ key: 'access-app', label: 'Access App', data: accessAppSettings || {}, el: AccessAppTab },
			{ key: 'feature-flags', label: 'Feature Flags', data: featureFlagsSettings || {}, el: FeatureFlagsTab },
			{
				key: 'principal-group-labels',
				label: 'Project Company Labels',
				data: principalGroupLabelsSettings || {},
				el: PrincipalGroupLabelsTab,
			},
		],
		[
			notificationsSettings,
			userDataSettings,
			companyPortalSettings,
			personalDashboardSettings,
			accessAppSettings,
			featureFlagsSettings,
			principalGroupLabelsSettings,
			partnerPortalSettings,
		],
	);

	const [data, setData] = useState<PossibleSettings>(tabs[defaultTabIndex]?.data || {});

	const tab = tabs[activeTab];

	const handleTabChange = useCallback(
		(tId: number) => {
			const tab = tabs[tId];
			setData(tab?.data || {});
			setActiveTab(tId);
		},
		[tabs],
	);

	const handleChange = (_data: PossibleSettings) => {
		setChanged(true);
		setData(_data as PossibleSettings);
	};

	const saveChanges = async () => {
		try {
			if (tab?.key === Settings.FeatureFlags) {
				await updateSettings<PossibleSettings>(Settings.FeatureFlags, data);
				queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
			} else {
				if (tab?.key === Settings.UserData) {
					const userData = data as UserDataSettings['settings'];
					if (userData?.minAge && userData?.maxAge) {
						if (userData.minAge! > userData.maxAge!) {
							EventEmitter.emit('toast', {
								type: 'error',
								message: 'Minimum age must be less than maximum age',
							});
							return;
						}
					}
				}

				await updateSettings<PossibleSettings>(tab?.key || '', data);
			}
			setChanged(false);
			EventEmitter.emit('toast', { type: 'success', message: 'Settings updated!' });
		} catch (_err) {
			console.error('Save error:', _err);
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update settings.' });
		}
	};

	const getCurrentTab = () => {
		const TabEl = tab?.el;
		if (!TabEl) return null;

		return (
			<TabEl
				data={data}
				onChange={handleChange}
				isDemoEnv={isDemoEnv}
				languagesSettings={languagesSettings}
				usesExternalZoneAccess={featureFlagsSettings?.USES_EXTERNAL_ZONE_ACCESS}
			/>
		);
	};

	return (
		<div className="min-h-full w-full p-4">
			<div className="flex mb-3">
				<div className="flex-grow" />
				<Button disabled={!hasChanged} variant="outlined" onClick={() => saveChanges()}>
					Save Changes
				</Button>
			</div>

			<Paper variant="outlined" className="nine-task-overrides">
				<Tabs value={activeTab} onChange={(value) => handleTabChange(value)}>
					<Tabs.TabList>
						{tabs.map((t, index) => (
							<Tabs.Tab key={t.key} value={index} label={t.label} />
						))}
					</Tabs.TabList>
					{tabs.map((t, index) => (
						<Tabs.TabPanel key={t.key} value={index} selectedValue={activeTab}>
							{getCurrentTab()}
						</Tabs.TabPanel>
					))}
				</Tabs>
			</Paper>
		</div>
	);
}
